// Library methods
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { StarIcon, StarIconOutline } from "src/assets/icons";

// Types
import { ExamLensType } from "src/services/exams/types";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Utils
import { isLensViewedByBothEyes } from "src/utils/exams/lensSimulationHelper";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

type LensSelectionCardType = {
  lens: ExamLensType;
  isDeviceReady: boolean;
  connection: HubConnection | null;
};

const LensSelectionCard = ({
  lens,
  isDeviceReady,
  connection,
}: LensSelectionCardType) => {
  const { t } = useTranslation();
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();

  const toggleView = useCallback(
    async (currentlyBeingViewed: boolean) => {
      if (!device?.id || !connection || !lens.name) return;

      try {
        const token = await getToken();
        await deviceUpdateLensParameter(
          token,
          device.id,
          {
            lensName: lens.name,
            currentlyBeingViewed,
          },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken, lens.name]
  );

  const toggleFavorite = useCallback(
    async (preferred: boolean) => {
      if (!device?.id || !connection || !lens.name) return;

      try {
        const token = await getToken();
        await deviceUpdateLensParameter(
          token,
          device.id,
          {
            lensName: lens.name,
            preferred,
            eye: eyeTypeEnums.BOTH,
          },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken, lens.name]
  );

  return (
    <Box
      onClick={() => {
        if (!isDeviceReady) return;
        toggleView(!isLensViewedByBothEyes(lens));
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "4px",
        p: "8px",
        border: isLensViewedByBothEyes(lens)
          ? "1px solid #E2772E"
          : "1px solid #5B6D7A40",
        borderRadius: "4px",
        minWidth: 0,
        cursor: isDeviceReady ? "pointer" : "default",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography fontSize="14px" fontWeight={600} noWrap>
          {lens.name}
        </Typography>
        <Typography fontSize="12px" noWrap>
          {t(lens.lensType ?? "")}
        </Typography>
      </Box>
      <Box display="flex" gap="8px">
        <IconButton
          aria-label="favorite-lens"
          disabled={!isDeviceReady}
          onClick={(event) => {
            event.stopPropagation();
            const newVal = !(
              lens?.preferred?.leftEye && lens?.preferred?.rightEye
            );
            toggleFavorite(newVal);
          }}
          sx={{ p: 0 }}
        >
          {lens?.preferred?.leftEye && lens?.preferred?.rightEye ? (
            <StarIcon width="20px" height="20px" style={{ color: "#E2772E" }} />
          ) : (
            <StarIconOutline
              width="20px"
              height="20px"
              style={{ color: "#000" }}
            />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default LensSelectionCard;
